"use client";

import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/context/lang";
import { Logo } from "@/components/logo";

export default function CookiesPage() {
  const { T, lang } = useLang();

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center gap-4 px-6 py-4">
          <Link href="/" className="flex items-center gap-2 text-neutral-500 hover:text-cyan-600 transition-colors">
            <ArrowLeft size={18} />
            <span className="text-sm">{lang === "tr" ? "Ana Sayfa" : "Home"}</span>
          </Link>
          <div className="ml-auto">
            <Logo size="sm" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-8 md:p-12">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">Cookie Policy</h1>
          <p className="text-sm text-neutral-400 mb-8">Last updated: April 7, 2026</p>

          <div className="prose prose-neutral max-w-none text-neutral-600 space-y-6 text-[15px] leading-relaxed">

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">1. Introduction</h2>
              <p>
                This Cookie Policy explains how CreateNano LLC ("Company," "we," "us," or "our"), a limited
                liability company organized under the laws of the State of New Mexico, USA, uses cookies and
                similar tracking technologies on the SpeakRoar platform ("Platform").
              </p>
              <p>
                By continuing to use the Platform, you acknowledge and consent to the use of cookies as
                described in this Cookie Policy. If you do not agree, you should adjust your browser settings
                accordingly or discontinue use of the Platform.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">2. What Are Cookies?</h2>
              <p>
                Cookies are small text files that are placed on your device (computer, tablet, or mobile phone)
                by websites you visit. They are widely used to make websites function properly, improve user
                experience, remember your preferences, and collect usage statistics.
              </p>
              <p>
                Cookies do not directly access your personal files; however, they may contain technical
                information such as browser type, device identifiers, and session data.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">3. Types of Cookies We Use</h2>
              <p>The Platform uses the following types of cookies and similar technologies:</p>

              <div className="mt-4 space-y-4">
                <div className="rounded-xl border border-neutral-100 bg-neutral-50 p-4">
                  <h3 className="font-semibold text-neutral-800 mb-1">a) Essential Cookies (Strictly Necessary)</h3>
                  <p className="text-sm">
                    These cookies are required for the Platform to function correctly. Without them, core
                    features such as authentication, navigation, and security protections would not work.
                    These cookies cannot be disabled.
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2 text-sm">
                    <li>Session identifier cookie (session ID)</li>
                    <li>Authentication token cookie</li>
                    <li>Security cookie (CSRF token)</li>
                  </ul>
                </div>

                <div className="rounded-xl border border-neutral-100 bg-neutral-50 p-4">
                  <h3 className="font-semibold text-neutral-800 mb-1">b) Functional Cookies (Preference Cookies)</h3>
                  <p className="text-sm">
                    Functional cookies remember your choices and preferences to provide a more personalized
                    experience on the Platform.
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2 text-sm">
                    <li>Language preference cookie</li>
                    <li>Theme preference (light/dark mode)</li>
                    <li>Last-used voice model preference</li>
                  </ul>
                </div>

                <div className="rounded-xl border border-neutral-100 bg-neutral-50 p-4">
                  <h3 className="font-semibold text-neutral-800 mb-1">c) Analytics Cookies (Performance Cookies)</h3>
                  <p className="text-sm">
                    Analytics cookies help us understand how visitors interact with the Platform by collecting
                    anonymized usage data. This information is used to improve performance and user experience.
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2 text-sm">
                    <li>Page view statistics</li>
                    <li>User interaction data (clicks, scrolling behavior)</li>
                    <li>Error reporting data</li>
                    <li>Performance measurement data</li>
                  </ul>
                </div>

                <div className="rounded-xl border border-neutral-100 bg-neutral-50 p-4">
                  <h3 className="font-semibold text-neutral-800 mb-1">d) Local Storage</h3>
                  <p className="text-sm">
                    In addition to cookies, the Platform may use your browser's local storage (localStorage)
                    feature. Local storage is used to persist user session information, application settings,
                    and cached data to improve load times and overall experience.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">4. Third-Party Cookies</h2>
              <p>
                The Platform may use third-party cookies to enhance service quality and facilitate certain
                operations. These cookies may be placed for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Payment Processing:</strong> Cookies set by payment service providers to enable secure transactions</li>
                <li><strong>Analytics Services:</strong> Cookies from web analytics tools (e.g., Google Analytics) to collect aggregated usage data</li>
                <li><strong>Content Delivery Networks (CDN):</strong> Infrastructure cookies that help the Platform load quickly and reliably</li>
              </ul>
              <p className="mt-2">
                Third-party cookies are governed by the respective third party's own privacy and cookie
                policies. CreateNano LLC is not responsible for the cookie practices of third-party providers.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">5. Cookie Retention Periods</h2>
              <p>Cookies are stored on your device for varying durations depending on their type:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Session Cookies:</strong> Automatically deleted when you close your browser</li>
                <li><strong>Persistent Cookies:</strong> Remain on your device until they expire or are manually deleted (typically between 30 days and 1 year)</li>
                <li><strong>Local Storage Data:</strong> Persists until manually cleared by the user or until the associated account is closed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">6. How to Manage and Disable Cookies</h2>
              <p>
                You can manage, restrict, or delete cookies through your browser settings. Please note that
                disabling essential cookies may prevent the Platform from functioning properly.
              </p>
              <p className="mt-2">To adjust cookie settings, refer to the instructions for your browser:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions → Manage and delete cookies and site data</li>
              </ul>
              <p className="mt-2">
                If you choose to disable all cookies, some features of the Platform may become unavailable or
                may not function as intended. CreateNano LLC assumes no responsibility for diminished
                functionality resulting from disabled cookies.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">7. Cookies and Personal Data</h2>
              <p>
                Data collected through cookies may constitute personal information under applicable law.
                For detailed information on how we collect, use, and protect your personal data, please
                review our
                <Link href="/privacy" className="text-cyan-600 hover:underline mx-1">Privacy Policy</Link>
                and
                <Link href="/ccpa" className="text-cyan-600 hover:underline mx-1">CCPA Notice</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">8. Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by applicable law, CreateNano LLC:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Does not guarantee absolute security of cookies or data transmitted via cookies</li>
                <li>Is not responsible for the behavior or practices of third-party cookies</li>
                <li>Shall not be held liable for any direct or indirect damages arising from the use of cookies</li>
                <li>Is not responsible for cookies failing to function as expected due to browser incompatibilities or user configurations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">9. Changes to This Policy</h2>
              <p>
                CreateNano LLC reserves the right to modify this Cookie Policy at any time without prior
                notice. Updates become effective as soon as they are published on the Platform. We encourage
                you to review this page periodically to stay informed of any changes.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">10. Contact Us</h2>
              <p>
                If you have questions or concerns about this Cookie Policy, please reach out to us through
                the contact channels available on the Platform or by emailing us at the address listed on
                our website.
              </p>
            </section>

          </div>
        </div>

        {/* Footer Links */}
        <div className="flex flex-wrap justify-center gap-4 mt-8 text-sm text-neutral-400">
          <Link href="/privacy" className="hover:text-cyan-600 transition-colors">Privacy Policy</Link>
          <span>·</span>
          <Link href="/terms" className="hover:text-cyan-600 transition-colors">Terms of Service</Link>
          <span>·</span>
          <Link href="/ccpa" className="hover:text-cyan-600 transition-colors">CCPA Notice</Link>
        </div>
      </div>
    </div>
  );
}
